import { folderName } from "~/utils/allocationPackageOps"
import type { PackageFolder } from "./types"

type FolderLink = Pick<PackageFolder, "id" | "parentId">

/** The folder and its parent, outermost first. */
export function folderPath(folders: PackageFolder[], folderId: string | null) {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))
  const folder = folderId === null ? undefined : byId.get(folderId)
  if (!folder) return []
  const parent = folder.parentId === null ? undefined : byId.get(folder.parentId)
  return parent ? [parent, folder] : [folder]
}

/** The folders and every subfolder under them. */
export function withSubfolders(folders: FolderLink[], folderIds: Iterable<string>) {
  const picked = new Set(folderIds)
  for (const folder of folders) {
    if (folder.parentId !== null && picked.has(folder.parentId)) picked.add(folder.id)
  }
  return picked
}

/** Why a folder can't take this name among siblings whose names are taken (lower-cased, trimmed). */
export function folderNameError(value: string, taken: Set<string>) {
  const parsed = folderName.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message
  if (taken.has(parsed.data.toLowerCase())) return "Another folder here already carries this name."
  return undefined
}

/** The top-level folder a folder sits in: its parent, or itself. */
export function topFolderId(folders: FolderLink[], folderId: string) {
  return folders.find((folder) => folder.id === folderId)?.parentId ?? folderId
}

/** "New subfolder", or "New subfolder 2" and on when the name is taken (case and outer spaces ignored). */
export function freeName(base: string, taken: string[]) {
  const keys = new Set(taken.map((name) => name.trim().toLowerCase()))
  if (!keys.has(base.toLowerCase())) return base
  let n = 2
  while (keys.has(`${base} ${n}`.toLowerCase())) n++
  return `${base} ${n}`
}

/** A duplicate's name: "AW26 (copy)", then "AW26 (copy 2)" and on. */
export function copyName(name: string, taken: string[]) {
  const keys = new Set(taken.map((other) => other.trim().toLowerCase()))
  const base = `${name.trim()} (copy)`
  if (!keys.has(base.toLowerCase())) return base
  let n = 2
  while (keys.has(`${name.trim()} (copy ${n})`.toLowerCase())) n++
  return `${name.trim()} (copy ${n})`
}

/** Why the folder can't become a subfolder of the target, or undefined when it can. */
export function cannotNestReason(folders: PackageFolder[], folderId: string, targetId: string) {
  const folder = folders.find((other) => other.id === folderId)
  const target = folders.find((other) => other.id === targetId)
  if (!folder || !target || folder.id === target.id) return "A folder can't go inside itself."
  if (target.parentId !== null) return `${target.name} is already a subfolder.`
  if (folders.some((other) => other.parentId === folder.id)) {
    return `${folder.name} holds subfolders, so it can't become a subfolder itself.`
  }
  if (folder.parentId === target.id) return `${folder.name} is already in ${target.name}.`
  return undefined
}

/** The folder of the selection: the folder itself, or the folder a selected package sits in. */
export function selectionFolderId(
  selection: { kind: "package" | "folder"; id: string } | null,
  packages: { id: string; folderId: string | null }[],
) {
  if (selection?.kind === "folder") return selection.id
  return packages.find((pkg) => pkg.id === selection?.id)?.folderId ?? null
}

/**
 * The sibling ids in their new order after placing `id` before or after
 * `targetId`. Siblings are given in their current display order.
 */
export function placeAmong(
  siblingIds: string[],
  id: string,
  targetId: string,
  position: "before" | "after",
) {
  const rest = siblingIds.filter((other) => other !== id)
  const at = rest.indexOf(targetId)
  if (at === -1) return siblingIds
  rest.splice(position === "before" ? at : at + 1, 0, id)
  return rest
}

/** The packages in the folder and its subfolders. */
export function packagesInFolder<T extends { folderId: string | null }>(
  packages: T[],
  folders: FolderLink[],
  folderId: string,
) {
  const ids = withSubfolders(folders, [folderId])
  return packages.filter((pkg) => pkg.folderId !== null && ids.has(pkg.folderId))
}

/** The folder a package sits in: what "Follow" points at, whether or not it follows today. */
export function packageFolder(folders: PackageFolder[], folderId: string | null) {
  return folderId === null ? undefined : folders.find((folder) => folder.id === folderId)
}
